// Drop-in replacements for the React Query hooks the copied ArcDisk
// components call (lib/hooks/useProjects, useGrades, useDisc, useMedia in the
// prototype). Same names, same `{ data, isLoading, error }` shape, but the
// data comes from the bundled fixtures filtered through the demo state, so a
// component never knows it is not talking to the ArcDisk API.
import { useMemo } from 'react'
import { useAllDemoState, useProjectDemoState } from './demoState'
import { deriveDashboard, deriveGrades, deriveSkillEvidence, deriveStudentDisc } from './derive'
import { projectFixtures, studentFixtures } from './fixtures'
import type { ProjectDashboard, ProjectReview, StudentDiscFixture, SubmissionItem } from './fixtures'

export { useAssetUrl } from './media'

interface Result<T> {
  data: T | undefined
  isLoading: boolean
  isError: boolean
  error: Error | null
}

function ok<T>(data: T | undefined): Result<T> {
  return { data, isLoading: false, isError: false, error: null }
}
function notFound<T>(what: string): Result<T> {
  return { data: undefined, isLoading: false, isError: true, error: new Error(`${what} is not part of the demo`) }
}

export function useProjectDashboard(adProjectId: string): Result<ProjectDashboard> {
  const state = useProjectDemoState(adProjectId)
  const fixture = projectFixtures[adProjectId]
  const data = useMemo(
    () => (fixture ? deriveDashboard(fixture.dashboard, new Set(state.revealed)) : undefined),
    [fixture, state.revealed],
  )
  return fixture ? ok(data) : notFound('Project')
}

export function useProjectReview(adProjectId: string | null | undefined): Result<ProjectReview> {
  const fixture = adProjectId ? projectFixtures[adProjectId] : undefined
  return fixture ? ok(fixture.review) : notFound('Project')
}

export function useProjectGrades(adProjectId: string) {
  const state = useProjectDemoState(adProjectId)
  const fixture = projectFixtures[adProjectId]
  const data = useMemo(
    () => (fixture ? deriveGrades(fixture.grades, new Set(state.revealed)) : undefined),
    [fixture, state.revealed],
  )
  return fixture ? ok(data) : notFound('Project')
}

export function useStudentSubmissions(adProjectId: string | undefined, studentId: number | undefined): Result<SubmissionItem[]> {
  const fixture = adProjectId ? projectFixtures[adProjectId] : undefined
  if (!fixture || studentId == null) return ok<SubmissionItem[]>(undefined)
  return ok(fixture.submissions[String(studentId)] ?? [])
}

/** A student counts as graded once ANY demo project has revealed them. */
export function useIsStudentGraded(studentId: number): boolean {
  const all = useAllDemoState()
  return Object.values(all).some(p => p.revealed.includes(studentId))
}

export function useDiscSkills(studentId: number | string): Result<StudentDiscFixture> {
  const id = Number(studentId)
  const graded = useIsStudentGraded(id)
  const fixture = studentFixtures[id]
  const data = useMemo(() => (fixture ? deriveStudentDisc(fixture, graded) : undefined), [fixture, graded])
  return fixture ? ok(data) : notFound('Student')
}

export function useSkillEvidence(studentId: number | string | null | undefined, skillId: string | null | undefined) {
  const all = useAllDemoState()
  const data = useMemo(() => {
    if (!studentId || !skillId) return undefined
    const revealed = new Set(Object.values(all).flatMap(p => p.revealed))
    return { items: deriveSkillEvidence(Object.values(projectFixtures), Number(studentId), skillId, revealed) }
  }, [all, studentId, skillId])
  return ok(data)
}

/** Which demo project a student belongs to (students appear in one project each). */
export function projectIdForStudent(studentId: number): string | undefined {
  return Object.values(projectFixtures).find(f => f.dashboard.students.some(s => s.student_id === studentId))?.dashboard.meta
    .project_id
}
